// ===== Packages =====
import {
    useEffect,
    useMemo,
    useState,
}                   from 'react';

/** Public categories -> map to your max image dimensions */
export type MediaQuality = 'small' | 'medium' | 'large';

// ** IMPORTANT **: Keep in sync with functions/src/index.ts
const SMALL_THUMB_MAX_HEIGHT = 300;
const SMALL_THUMB_MAX_WIDTH = 300;
const MEDIUM_THUMB_MAX_HEIGHT = 1000;
const MEDIUM_THUMB_MAX_WIDTH = 1000;
const LARGE_THUMB_MAX_HEIGHT = 2500;
const LARGE_THUMB_MAX_WIDTH = 2500;

export const MEDIA_DIMENSIONS: Record<
    MediaQuality,
    {
        maxWidth: number,
        maxHeight: number,
    }
> = {
    small: {
        maxWidth: SMALL_THUMB_MAX_WIDTH,
        maxHeight: SMALL_THUMB_MAX_HEIGHT,
    },
    medium: {
        maxWidth: MEDIUM_THUMB_MAX_WIDTH,
        maxHeight: MEDIUM_THUMB_MAX_HEIGHT,
    },
    large: {
        maxWidth: LARGE_THUMB_MAX_WIDTH,
        maxHeight: LARGE_THUMB_MAX_HEIGHT,
    },
};

type UseMediaQualityOptions = {
    /** If true, try not to downscale unless it’s clearly slow/weak. Default: false */
    preferHighQuality?: boolean;
    /** Throttle resize handling (ms). Default: 150 */
    resizeThrottleMs?: number;
};

type Metrics = {
    vw: number;
    vh: number;
    dpr: number;
    displayMaxPx: number; // max(vw, vh) * dpr
    // Network Information API (optional)
    saveData?: boolean;
    effectiveType?: string; // 'slow-2g' | '2g' | '3g' | '4g' (not standardized everywhere)
    downlinkMbps?: number;  // approximate
    rttMs?: number;
    // Device hints
    deviceMemoryGb?: number;      // e.g. 4
    hardwareConcurrency?: number; // e.g. 8
};

type Result = {
    quality: MediaQuality;
    /** Brief human-readable reasons for the chosen quality (useful for debugging/telemetry) */
    reasons: string[];
    metrics: Metrics;
};

function nowMetrics(): Metrics {
    // SSR guards
    if (typeof window === 'undefined') {
        return {
            vw: 1024,
            vh: 768,
            dpr: 1,
            displayMaxPx: 1024,
        };
    }

    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const dpr = window.devicePixelRatio || 1;

    // Device hints
    const deviceMemoryGb = typeof navigator !== 'undefined'
        && typeof (navigator as any).deviceMemory === 'number'
        ? (navigator as any).deviceMemory
        : undefined;

    const hardwareConcurrency = typeof navigator !== 'undefined'
        && typeof navigator.hardwareConcurrency === 'number'
        ? navigator.hardwareConcurrency
        : undefined;

    // Network Info API (not available on Safari/iOS)
    const connection = typeof navigator !== 'undefined'
        && 'connection' in navigator
        ? (navigator as any).connection
        : undefined;

    const saveData = connection?.saveData;
    const effectiveType = connection?.effectiveType as string | undefined;
    const downlinkMbps = typeof connection?.downlink === 'number' ? connection.downlink : undefined;
    const rttMs = typeof connection?.rtt === 'number' ? connection.rtt : undefined;

    const displayMaxPx = Math.max(vw, vh) * dpr;

    return {
        vw,
        vh,
        dpr,
        displayMaxPx,
        saveData,
        effectiveType,
        downlinkMbps,
        rttMs,
        deviceMemoryGb,
        hardwareConcurrency,
    };
}

function pickBaseByViewport(displayMaxPx: number): { quality: MediaQuality; reason: string } {
    // Choose based on *rendered* pixel demand (CSS px * DPR).
    // Tweak thresholds as desired.
    if (displayMaxPx >= 1600) {
        return {
            quality: 'large',
            reason: 'Big viewport × DPR',
        };
    }
    if (displayMaxPx >= 700) {
        return {
            quality: 'medium',
            reason: 'Moderate viewport × DPR',
        };
    }
    return {
        quality: 'small',
        reason: 'Small viewport × DPR',
    };
}

function degrade(quality: MediaQuality): MediaQuality {
    return quality === 'large' ? 'medium' : 'small';
}

function applyNetworkAndDeviceAdjustments(
    startQuality: MediaQuality,
    metrics: Metrics,
    preferHighQuality: boolean,
): {
    quality: MediaQuality,
    reasons: string[],
} {
    let quality = startQuality;
    const reasons: string[] = [];

    const {
        saveData,
        effectiveType,
        downlinkMbps,
        rttMs,
        deviceMemoryGb,
        hardwareConcurrency,
    } = metrics;

    // Heuristics for "slow network"
    const verySlow = (typeof downlinkMbps === 'number' && downlinkMbps < 0.5)
        || (typeof rttMs === 'number' && rttMs > 800)
        || effectiveType === 'slow-2g';
    const slow = saveData === true
        || effectiveType === '2g'
        || effectiveType === '3g'
        || (typeof downlinkMbps === 'number' && downlinkMbps < 1.5)
        || (typeof rttMs === 'number' && rttMs > 300);

    // Heuristics for lower-end devices
    const lowMemory = typeof deviceMemoryGb === 'number' && deviceMemoryGb <= 1;
    const lowCores = typeof hardwareConcurrency === 'number' && hardwareConcurrency <= 2;

    if (verySlow) {
        if (quality !== 'small') {
            quality = 'small';
            reasons.push('Very slow network → force small');
        } else {
            reasons.push('Very slow network');
        }
    } else if (slow) {
        // respect preferHighQuality by not double-downgrading
        if (!preferHighQuality) {
            const prev = quality;
            quality = degrade(quality);
            if (prev !== quality) reasons.push('Slow network → downgraded');
        } else {
            reasons.push('Slow network (preferHighQuality=true, no downgrade)');
        }
    }

    if (lowMemory || lowCores) {
        if (!preferHighQuality) {
            const prev = quality;
            quality = degrade(quality);
            if (prev !== quality) reasons.push('Low device capability → downgraded');
        } else {
            reasons.push('Low device capability (preferHighQuality=true, no downgrade)');
        }
    }

    if (saveData) reasons.push('Data Saver enabled');
    if (effectiveType) reasons.push(`effectiveType=${effectiveType}`);
    if (typeof downlinkMbps === 'number') reasons.push(`downlink≈${downlinkMbps}Mbps`);
    if (typeof rttMs === 'number') reasons.push(`rtt≈${rttMs}ms`);
    if (typeof deviceMemoryGb === 'number') reasons.push(`deviceMemory≈${deviceMemoryGb}GB`);
    if (typeof hardwareConcurrency === 'number') reasons.push(`cores=${hardwareConcurrency}`);

    return { quality, reasons };
}

function throttle<T extends(...args: any[]) => void>(fn: T, ms: number): T {
    let last = 0;
    let timeout: number | undefined;
    let pendingArgs: any[] | null = null;

    const invoke = (args: unknown[]): void => {
        last = Date.now();
        fn(...args);
    };

    return ((...args: any[]) => {
        const now = Date.now();
        const remaining = ms - (now - last);
        if (remaining <= 0) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = undefined;
            }
            invoke(args);
        } else {
            pendingArgs = args;
            if (!timeout) {
                timeout = window.setTimeout(() => {
                    timeout = undefined;
                    if (pendingArgs) {
                        invoke(pendingArgs);
                        pendingArgs = null;
                    }
                }, remaining);
            }
        }
    }) as T;
}

/**
 * React hook that decides which image size to serve: "small" | "medium" | "large".
 * Reacts to viewport changes and (where supported) network changes.
 */
export default function useMediaQuality(options: UseMediaQualityOptions = {}): Result {
    const { preferHighQuality = false, resizeThrottleMs = 150 } = options;

    const [metrics, setMetrics] = useState<Metrics>(() => nowMetrics());

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const onResize = throttle(() => setMetrics(nowMetrics()), resizeThrottleMs);
        window.addEventListener('resize', onResize);

        // Listen for network changes when supported
        const connection = typeof navigator !== 'undefined'
            ? (navigator as any).connection
            : undefined;

        const onConnChange = (): void => setMetrics(nowMetrics());
        if (connection && typeof connection.addEventListener === 'function') {
            connection.addEventListener('change', onConnChange);
        }

        // Initial sync in case something changed between mount and now
        setMetrics(nowMetrics());

        return () => {
            window.removeEventListener('resize', onResize);
            if (connection && typeof connection.removeEventListener === 'function') {
                connection.removeEventListener('change', onConnChange);
            }
        };
    }, [
        resizeThrottleMs,
    ]);

    const result = useMemo<Result>(() => {
        const base = pickBaseByViewport(metrics.displayMaxPx);
        const adjusted = applyNetworkAndDeviceAdjustments(base.quality, metrics, preferHighQuality);
        const reasons = [base.reason, ...adjusted.reasons];
        return { quality: adjusted.quality, reasons, metrics };
    }, [
        metrics,
        preferHighQuality,
    ]);

    return result;
}
